import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react'
import {
  authApi,
  type MeUser,
  type RegisterPayload,
  type UpdateMePayload,
} from '@/api/auth'
import { getStoredToken, setStoredToken } from '@/api/client'
import { useWarehouse } from '@/context/WarehouseContext'

type AuthContextValue = {
  token: string | null
  user: MeUser | null
  isLoading: boolean
  isAuthenticated: boolean
  login: (phone: string, password: string) => Promise<MeUser>
  register: (payload: RegisterPayload) => Promise<MeUser>
  acceptOAuthToken: (token: string) => Promise<MeUser | null>
  linkPhone: (phone: string, password: string) => Promise<MeUser>
  logout: () => void
  fetchMe: () => Promise<MeUser | null>
  updateProfile: (payload: UpdateMePayload) => Promise<MeUser>
}

const AuthContext = createContext<AuthContextValue | null>(null)
const isReactSnap =
  typeof navigator !== 'undefined' &&
  navigator.userAgent.includes(
    'ReactSnap',
  )

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { selectedWarehouseId } = useWarehouse()
  const [token, setToken] =
    useState<string | null>(
      () =>
        isReactSnap
          ? null
          : getStoredToken(),
    )
  const [user, setUser] = useState<MeUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const fetchMe = useCallback(async (): Promise<MeUser | null> => {
    if (!token) {
      setUser(null)
      return null
    }
    try {
      const me = await authApi.getMe()
      setUser(me)
      return me
    } catch {
      setStoredToken(null)
      setToken(null)
      setUser(null)
      return null
    }
  }, [token])

  useEffect(() => {
    if (isReactSnap) return
    setToken(getStoredToken())
  }, [selectedWarehouseId])

  useEffect(() => {
    if (isReactSnap) {
      setUser(null)
      setIsLoading(false)
      return
    }

    if (!token) {
      setUser(null)
      setIsLoading(false)
      return
    }

    void fetchMe()
      .finally(() =>
        setIsLoading(false),
      )
  }, [
    token,
    fetchMe,
  ])

  const login = useCallback(async (phone: string, password: string): Promise<MeUser> => {
    const data = await authApi.login({ phone, password })
    if (data.token) {
      setStoredToken(data.token)
      setToken(data.token)
    }
    setUser(data)
    return data
  }, [])

  const register = useCallback(async (payload: RegisterPayload): Promise<MeUser> => {
    const data = await authApi.register(payload)
    if (data.token) {
      setStoredToken(data.token)
      setToken(data.token)
    }
    setUser(data)
    return data
  }, [])

  const linkPhone = useCallback(async (phone: string, password: string): Promise<MeUser> => {
    const me = await authApi.linkPhone({ phone, password })
    if (me.token) {
      setStoredToken(me.token)
      setToken(me.token)
    }
    setUser(me)
    return me
  }, [])

  const acceptOAuthToken = useCallback(async (newToken: string): Promise<MeUser | null> => {
    setStoredToken(newToken)
    setToken(newToken)
    try {
      const me = await authApi.getMe()
      setUser(me)
      return me
    } catch {
      setStoredToken(null)
      setToken(null)
      setUser(null)
      return null
    }
  }, [])

  const logout = useCallback(() => {
    setStoredToken(null)
    setToken(null)
    setUser(null)
  }, [])

  const updateProfile = useCallback(async (payload: UpdateMePayload): Promise<MeUser> => {
    const updated = await authApi.updateMe(payload)
    setUser(updated)
    return updated
  }, [])

  const value: AuthContextValue = {
    token,
    user,
    isLoading,
    isAuthenticated: !!user && !!token,
    login,
    register,
    acceptOAuthToken,
    linkPhone,
    logout,
    fetchMe,
    updateProfile,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
